import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, UserX } from "lucide-react";

interface ReviewRow {
  id: string;
  user_id: string;
  email: string | null;
  last_sign_in_at: string | null;
  content_summary: Record<string, number>;
  status: string;
  flagged_at: string;
}

const InactivityReviewTab = () => {
  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("account_inactivity_review")
      .select("id,user_id,email,last_sign_in_at,content_summary,status,flagged_at")
      .eq("status", "pending")
      .order("flagged_at", { ascending: true });
    setLoading(false);
    if (error) toast.error(error.message);
    else setRows((data as ReviewRow[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const decide = async (row: ReviewRow, action: "anonymize" | "purge" | "dismiss") => {
    setActing(row.id);
    try {
      const { error } = await supabase.functions.invoke("retention-admin-delete", {
        body: { user_id: row.user_id, disposition: action, review_id: row.id },
      });
      if (error) throw error;
      toast.success(
        action === "dismiss"
          ? "Dismissed — user will be re-flagged later if still inactive."
          : `Account ${action === "anonymize" ? "anonymized" : "purged"}.`,
      );
      await load();
    } catch (e: any) {
      toast.error(e.message || "Action failed");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;
  }

  if (rows.length === 0) {
    return (
      <div className="text-center py-20 text-muted-foreground">
        <UserX className="h-8 w-8 mx-auto mb-3 opacity-50" />
        <p className="text-lg mb-1">No accounts pending review</p>
        <p className="text-sm">The retention sweep will flag inactive accounts here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <h2 className="font-display text-xl font-semibold mb-2">Inactive accounts pending review</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Accounts inactive 36+ months that have public contributions. Choose how to retire them.
      </p>
      {rows.map((r) => {
        const total = Object.values(r.content_summary || {}).reduce((a, b) => a + (b as number), 0);
        return (
          <div key={r.id} className="bg-card border border-border rounded-xl p-5">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="font-medium">{r.email ?? r.user_id}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  Last sign-in: {r.last_sign_in_at ? new Date(r.last_sign_in_at).toLocaleDateString() : "never"} · Flagged {new Date(r.flagged_at).toLocaleDateString()}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Content: {total} items ·{" "}
                  {Object.entries(r.content_summary || {})
                    .filter(([, v]) => (v as number) > 0)
                    .map(([k, v]) => `${k.replace("idea_", "")}: ${v}`)
                    .join(" · ") || "none"}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" disabled={!!acting} onClick={() => decide(r, "dismiss")}>
                  Dismiss
                </Button>
                <Button size="sm" variant="secondary" disabled={!!acting} onClick={() => decide(r, "anonymize")}>
                  Anonymize & delete
                </Button>
                <Button size="sm" variant="destructive" disabled={!!acting} onClick={() => decide(r, "purge")}>
                  {acting === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Purge everything"}
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default InactivityReviewTab;